import { useCallback, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useInviteProfessionalsToJobsMutation } from '~/domain/Jobs';
import {
  factoryGetEligibleCandidatesKey,
  selectInvitedCandidateIds,
  useGetEligibleCandidates,
} from '~/domain/PermanentJobs';
import { type IJob } from '~/models';
import { getJobDateInTimezone } from '~/utils/JobUtils';

/**
 * Drives the Interview Details invite flow: derives the availability date, tracks
 * the selection + confirmation state, and sends the selected candidates to this
 * interview — invalidating the candidate query on success so the just-invited rows
 * flip to "Sent". Also surfaces how many candidates are already invited. Kept as a
 * hook so `InterviewInviteSection` stays presentational and this is unit-testable.
 */
export const useInterviewInvite = (job: IJob) => {
  const queryClient = useQueryClient();
  const postingId = job.postingId;
  const interviewId = job.id;
  const officeId = job.offer_owner?.id;
  const date = getJobDateInTimezone(job);

  const { mutate: inviteProfessionalsToJobs, isPending } = useInviteProfessionalsToJobsMutation();

  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [showSuccess, setShowSuccess] = useState(false);
  // Remounting the list (via listKey) after a send resets its internal selection
  // and re-renders the freshly invited candidates as "Sent".
  const [listKey, setListKey] = useState(0);

  // Shares the list's query (same key → one fetch) to drive the invited count.
  const { data: candidates } = useGetEligibleCandidates(
    { postingId, date },
    { enabled: Boolean(postingId) },
  );
  const invitedCount = selectInvitedCandidateIds(candidates, interviewId).length;

  const invite = useCallback(() => {
    if (selectedIds.length === 0) return;

    inviteProfessionalsToJobs(
      { jobIds: [interviewId], professionalIds: selectedIds, officeId },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({
            queryKey: factoryGetEligibleCandidatesKey({ postingId, date }),
          });
          setShowSuccess(true);
          setSelectedIds([]);
          setListKey((key) => key + 1);
        },
      },
    );
  }, [inviteProfessionalsToJobs, interviewId, officeId, postingId, date, selectedIds, queryClient]);

  return {
    date,
    listKey,
    setSelectedIds,
    canInvite: selectedIds.length > 0,
    isInviting: isPending,
    invitedCount,
    showSuccess,
    closeSuccess: () => setShowSuccess(false),
    invite,
  };
};
